import React, { useCallback, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Utils from "../../utils";
import { Button, CircularProgress, Grid, IconButton, Stack } from "@mui/material";
import FeatherIcon from "feather-icons-react";
import ConfirmDialog from "../../components/confirm-dialog";
import { AppDataContext } from "../../components/context/app-data-context";
import TableComponent from "../../components/table/list";
import { useSnackbar } from "../../components/toast";
import { PHRASE_ADDED_FAILED, PHRASE_ADDED_SUCCESS, PHRASE_DELETED_FAILED, PHRASE_DELETED_SUCCESS } from "../../helpers/messages";
import AddPhrase from "./add-phrases";
import { CustomTooltip } from "../../consts";

function Tracker() {
    const navigate = useNavigate();
    const location = useLocation();
    const showSnackbar = useSnackbar();
    const [data, setData] = React.useState([]);
    const [trackerName, setTrackerName] = React.useState("");
    const [companyData, setCompanyData] = React.useState("");
    const [spinner, setSpinner] = React.useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [deleteIndex, setDeleteIndex] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(null);
    const AppDataContextDTO = useContext(AppDataContext);
    const [changeIndex, setChangeIndex] = React.useState(null);

    function setMainData() {
        Utils.sendProgressBar(true)
        setSpinner(true)

        const _data = location.state.tracker.phrases.map((text) => {
            return { text: text, edit: false };
        });
        setData(_data);

        setTrackerName(location.state.tracker.name);
        setCompanyData(location.state.company);
        AppDataContextDTO.setInsightName(location.state.tracker.name);
        setSpinner(false)
    }
    
    React.useEffect(() => {
        window.scrollTo(0, 0);
        if (location.state) { 
            setMainData();
        } else {
            navigate("/insights")
        }
    }, []);

    // const returnToTrackers = () => {
    //     navigate("/insights")
    // }

    // update the phrases in the company and campaigns
    // this function is called when the user add, edit or deletes a phrase
    const update = useCallback(async (updatedData) => {
        Utils.sendProgressBar(true);
        const phrases = updatedData.filter(z => z.text.trim()).map((z) => z.text);
        
        // update the tracker, phrases in the company
        let foundTrackers = companyData.trackers.find((t) => t.name === trackerName);
        if (foundTrackers) {
            foundTrackers.phrases = phrases;
            companyData.trackers.forEach((z) => delete z.campaigns);
            await Utils.genericMutation("updateCompany", {
                id: companyData.id,
                trackers: JSON.stringify(companyData.trackers),
            });
        }

        // update the phrases in each campaign tracker 
        const campaigns = [];
        companyData.campaigns.items.forEach((z) => {
            const foundTrackers2 = z.trackers.find((t) => t.name === trackerName);
            if (foundTrackers2) {
                foundTrackers2.phrases = phrases;
                campaigns.push({ id: z.id, trackers: JSON.stringify(z.trackers) });
            }
        });
        
        // update campaign trackers
        async function updateAll(campaign) {
            await Utils.genericMutation("updateCampaign", {
                id: campaign.id,
                trackers: campaign.trackers,
            });
        }
        await Promise.all(campaigns.map(updateAll));
        
        // update the company data in the location state
        updateTheStateData(phrases);
    }, [data, companyData, trackerName]);
    
    // Update the company data in the location state
    // This is important to ensure that the updated data is reflected in the UI after refresh
    const updateTheStateData = useCallback((phrases) => {
        const tracker = companyData.trackers.find((z) => z.name === trackerName);
        tracker.phrases = phrases;
        navigate(location.pathname, {
            replace: true,
            state: { company: companyData, tracker, updated: true }
        });
    }, [companyData, trackerName, navigate, location.pathname]);      

    const handleDeleteConfirm = useCallback(async () => {
        setIsLoading(true);
        try {
            const _data = Object.assign([], data);
            _data.splice(deleteIndex, 1);
            await update(_data);
            setData([..._data]);
            setDeleteConfirmOpen(false);
            setDeleteIndex(null);
            showSnackbar(PHRASE_DELETED_SUCCESS);
            setIsLoading(false);
        } catch (error) {
            showSnackbar(PHRASE_DELETED_FAILED, "error")
            setIsLoading(false);
        }

    }, [data, deleteIndex, update]);

    const renderConfirm = useCallback(() => {
        return (<ConfirmDialog
            open={deleteConfirmOpen}
            loading={isLoading}
            onClose={() => {
                setDeleteConfirmOpen(false);
                setDeleteIndex(null);
            }}
            onConfirm={handleDeleteConfirm}
            title="Confirm Delete"
            okText="Delete"
            message={`Are you sure you want to delete this phrase?`}
        />)
    }, [deleteConfirmOpen, isLoading, handleDeleteConfirm]);

    // method to add, update phrase
    const addPhraseAPI = async (index, phraseText) => {
        // check for duplicates
        const found = data.find((z, i) => z.text.trim().toLowerCase() === phraseText.trim().toLowerCase() && i !== index);
        if (found) {
            showSnackbar(`Phrase "${phraseText}" already exists.`, "error");
            return;
        }
        try {
            const _data = Object.assign([], data);
            _data[index].text = phraseText;
            _data[index].edit = !_data[index].edit;
            setChangeIndex(index);
            setData([..._data]);
            await update(_data);
            showSnackbar(PHRASE_ADDED_SUCCESS);
            setChangeIndex(null);
        } catch (error) {
            setChangeIndex(null);
            showSnackbar(PHRASE_ADDED_FAILED, "error");
        }
    }

    // define table headers
    const columns = [{
        label: `Word or Phrase for ${trackerName}`,
        key: "text",
        sortable: false,
        renderItem: (row, index) => (
            row.edit ? (
                <AddPhrase
                    key={index}
                    value={row.text}
                    onSave={(phraseText) => addPhraseAPI(index, phraseText)}
                />
            ) : (
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Stack
                        direction="row"
                        gap={3}
                    >
                        <span>{index + 1}.</span>
                        {row.text || "-"}
                    </Stack>
                    <Stack
                        direction="row"
                        gap={1}
                    >
                        <CustomTooltip title={"Edit"}>
                            <IconButton
                                aria-label="Edit"
                                size="small"
                                color="inherit"
                                onClick={() => {
                                    let updatedData = [...data];
                                    updatedData[index].edit = !updatedData[index].edit;
                                    setData(updatedData);
                                }}
                                disabled={true} // VOC-199 DISABLED FOR DEMO
                            >
                                {changeIndex === index ? (<CircularProgress size={20} />) : <FeatherIcon icon="edit-2" size={20} />}
                            </IconButton>
                        </CustomTooltip>
                        <CustomTooltip title={"Delete"}>
                            <IconButton
                                aria-label="Delete"
                                size="small"
                                color="inherit"
                                onClick={() => {
                                    setDeleteIndex(index);
                                    setDeleteConfirmOpen(true);
                                }}
                                disabled={true} // VOC-199 DISABLED FOR DEMO
                            >
                                <FeatherIcon icon="trash-2" size={20} />
                            </IconButton>
                        </CustomTooltip>
                    </Stack>
                </Stack>
            )
        )
    }];

    const headerActionButtons = () => {
        return (
            <Button
                variant="contained"
                onClick={() => {
                    const _data = Object.assign([], data);
                    _data.push({ text: "", edit: true });
                    setData(_data);
                }}
                startIcon={<FeatherIcon icon="plus-circle" size={20} />}
                disabled={true} // VOC-199 DISABLED FOR DEMO
            >
                Add Word or Phrase
            </Button>
        )
    }

    return (
        <Grid display="flex" flexDirection="column" flexGrow="1" overflow="auto">
            {renderConfirm()}
            <Grid
                className="vocalysed-home-upadted campaigns"
                container
                spacing={0}
                direction="column"
                flexWrap="nowrap"
                flexGrow={1}
                paddingTop={2}
                paddingX={3}
                paddingBottom={3}
                overflow="auto"
            >
                <TableComponent
                    headerActionButtons={headerActionButtons}
                    spinner={spinner}
                    data={data}
                    columns={columns}
                    pagination={false}
                    searchKeys={["text"]}
                    noDataMessage={"No phrases found."}
                />
            </Grid>
        </Grid>
    );
}

export default Tracker;
